"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosAgentCreditChargeSummarySchema = exports.APosAgentCreditsHistoryResponseSchema = exports.APosAgentCreditsHistoryRequestSchema = exports.APosAgentCreditsCheckoutResponseSchema = exports.APosAgentCreditsCheckoutRequestSchema = exports.APosAgentCreditsBundlesResponseSchema = exports.APosAgentCreditBundleSchema = exports.APosAgentAdditionalUsageUpdateSchema = exports.APosAgentAdditionalUsageSchema = exports.APosAgentCreditsBalanceResponseSchema = exports.APosAgentCreditsBalanceRequestSchema = void 0;
const zod_1 = require("zod");
const agent_credits_schema_1 = require("../../entities/agenticpos/agent-credits.schema");
/**
 * AgenticPOS AI Agent Credits — Customer-facing API schemas
 *
 * Wave 0 endpoints implemented:
 *   GET  /agents/credits/balance
 *   GET  /agents/credits/bundles
 *
 * Schemas for later Wave 1 endpoints (checkout, webhook, history)
 * are also declared here so they live in one place.
 */
// ---------- GET /agents/credits/balance ----------
exports.APosAgentCreditsBalanceRequestSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1),
    /** Optional operation name to get a preview of expected credit cost. */
    preview: agent_credits_schema_1.APosAgentCreditOperationSchema.optional(),
});
exports.APosAgentCreditsBalanceResponseSchema = zod_1.z.object({
    tenantId: zod_1.z.string(),
    balance: zod_1.z.number().nonnegative(),
    totalPurchased: zod_1.z.number().nonnegative(),
    totalGranted: zod_1.z.number().nonnegative(),
    totalConsumed: zod_1.z.number().nonnegative(),
    totalRefunded: zod_1.z.number().nonnegative(),
    totalDeducted: zod_1.z.number().nonnegative(),
    lastTransactionAt: zod_1.z.string().nullable(),
    /** Credits required to run the previewed operation at the tenant's resolved burn rate. */
    previewCredits: zod_1.z.number().int().nonnegative().nullable().optional(),
    /** Whether the tenant can afford the previewed operation right now. */
    canAfford: zod_1.z.boolean().nullable().optional(),
    // --- 2026-09-27, Ruby AI Actions wallet (all optional, additive) ---
    // Amounts are CREDITS (storage unit); UIs display ×RUBY_TOKENS_PER_CREDIT.
    /** Allowance remaining this period (0 once past allowancePeriodEnd). */
    allowanceBalance: zod_1.z.number().nonnegative().optional(),
    /** Allowance granted this period; null = unlimited / not metered. */
    allowanceIncluded: zod_1.z.number().nonnegative().nullable().optional(),
    allowancePeriodEnd: zod_1.z.string().nullable().optional(),
    /** Purchased bucket — equals `balance`. */
    purchasedBalance: zod_1.z.number().nonnegative().optional(),
    autoTopUp: zod_1.z.boolean().optional(),
    /** Tenant's AI tier package id (pkg_ai_default | pkg_ai_pro | pkg_pos_agents). */
    tierId: zod_1.z.string().nullable().optional(),
    lastUsedVibeId: zod_1.z.string().nullable().optional(),
    // --- 2026-10-11, Additional usage (owner: billing beyond tier allowance) ---
    overageUsed: zod_1.z.number().nonnegative().optional(),
    overageCap: zod_1.z.number().nonnegative().nullable().optional(),
    // --- 2026-10-17, live Ruby Token value (Super Admin → AI → Settings) ---
    /** Ruby Tokens per stored credit for DISPLAY (default 1000). */
    tokensPerCredit: zod_1.z.number().int().positive().optional(),
    /** USD value of one Ruby Token (default 0.00001). */
    usdPerRubyToken: zod_1.z.number().positive().optional(),
});
// --- 2026-10-11, Additional usage schemas ---
exports.APosAgentAdditionalUsageSchema = zod_1.z.object({
    enabled: zod_1.z.boolean(),
    capCredits: zod_1.z.number().nonnegative().nullable(),
    usedCredits: zod_1.z.number().nonnegative(),
    /** Ruby AI tier package ids — the client shows the checkout card only when the order holds one. */
    aiTierPackageIds: zod_1.z.array(zod_1.z.string()),
});
exports.APosAgentAdditionalUsageUpdateSchema = zod_1.z.object({
    enabled: zod_1.z.boolean(),
    capCredits: zod_1.z.number().nonnegative().nullable(),
    consentTextVersion: zod_1.z.string().max(40).optional(),
});
// ---------- GET /agents/credits/bundles ----------
exports.APosAgentCreditBundleSchema = zod_1.z.object({
    id: zod_1.z.string(), // Stripe product ID
    stripePriceId: zod_1.z.string(), // Stripe price ID
    label: zod_1.z.string(),
    description: zod_1.z.string().nullable().optional(),
    creditsPerUnit: zod_1.z.number().int().positive(),
    unitPriceUsd: zod_1.z.number().nonnegative(),
    currency: zod_1.z.string().length(3),
});
exports.APosAgentCreditsBundlesResponseSchema = zod_1.z.object({
    bundles: zod_1.z.array(exports.APosAgentCreditBundleSchema),
    minPurchaseCredits: zod_1.z.number().int().positive(),
});
// ---------- POST /agents/credits/checkout (Wave 1) ----------
exports.APosAgentCreditsCheckoutRequestSchema = zod_1.z.object({
    stripePriceId: zod_1.z.string().min(1),
    quantity: zod_1.z.number().int().positive(),
    idempotencyKey: zod_1.z.string().min(1),
});
exports.APosAgentCreditsCheckoutResponseSchema = zod_1.z.object({
    sessionId: zod_1.z.string(),
    url: zod_1.z.string().url(),
});
// ---------- GET /agents/credits/history (Wave 1) ----------
exports.APosAgentCreditsHistoryRequestSchema = zod_1.z.object({
    fromIso: zod_1.z.string().optional(),
    toIso: zod_1.z.string().optional(),
    types: zod_1.z.array(agent_credits_schema_1.APosAgentCreditTransactionTypeSchema).optional(),
    operations: zod_1.z.array(agent_credits_schema_1.APosAgentCreditOperationSchema).optional(),
    limit: zod_1.z.number().int().positive().max(100).default(20),
    exclusiveStartKey: zod_1.z.string().optional(),
});
exports.APosAgentCreditsHistoryResponseSchema = zod_1.z.object({
    items: zod_1.z.array(agent_credits_schema_1.APosAgentCreditLedgerRowSchema),
    lastEvaluatedKey: zod_1.z.string().nullable().optional(),
    count: zod_1.z.number().int().nonnegative(),
});
// ---------- Common breakdown wrapper for customer-facing responses ----------
exports.APosAgentCreditChargeSummarySchema = zod_1.z.object({
    creditsUsed: zod_1.z.number().int().nonnegative(),
    newBalance: zod_1.z.number().int().nonnegative(),
    breakdown: zod_1.z.array(agent_credits_schema_1.APosAgentCreditBreakdownEntrySchema),
});
//# sourceMappingURL=agent-credits.schema.js.map